import React, { useState, useEffect } from "react";
import axios from "axios";
import { Bar } from "react-chartjs-2";
import { Button, ButtonGroup, TextField, RadioGroup, FormControlLabel, Radio } from "@material-ui/core";
import { apiATC } from "../../config";

const COULEURS = ["#F7F6CF", "#B6D8F2", "#F4CFDF", "#5784BA", "#DB6A8F", "#0b75f7ff", "#f7ab54ff"];
const MOIS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const JOURS_FR = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

const SuiviProduction = () => {
    const [periode, setPeriode] = useState("week");
    const [dateVal, setDateVal] = useState(getDateParDefaut("week"));
    const [data, setData] = useState(null);
    const [metric, setMetric] = useState("articles"); // "articles" ou "temps"

    const handleChangePeriode = (newPeriode) => {
        setPeriode(newPeriode);
        setDateVal(getDateParDefaut(newPeriode));
    };

    const fetchData = async (periodeChoisie, date_val) => {
        try {
            const url = `${apiATC}/analyse/analyse_production.php?periode=${periodeChoisie}&date_val=${date_val}`;
            const res = await axios.get(url);
            setData(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchData(periode, dateVal);
    }, [periode, dateVal]);

    if (!data) return <p>Chargement...</p>;

    const totalArticles = (data.recap.Classique.nb_articles || 0) + (data.recap.SAV.nb_articles || 0);
    const totalMinutes = (data.recap.Classique.temps_total || 0) + (data.recap.SAV.temps_total || 0);

    // --- 🔥 Calcul Moyenne & Ecart-type sur totaux par période ---
    const valuesByPeriod = Object.entries(data.graph).map(([_, ouvriers]) => {
        let totalArticles = 0;
        let totalMinutes = 0;
        Object.values(ouvriers).forEach(stats => {
            totalArticles += stats.nb_articles || 0;
            totalMinutes += stats.temps_total || 0;
        });
        return { totalArticles, totalMinutes };
    });

    const filtered = valuesByPeriod.filter(v => v.totalArticles > 0 || v.totalMinutes > 0);

    const articlesArr = filtered.map(v => v.totalArticles);
    const minutesArr = filtered.map(v => v.totalMinutes);

    const moyArticles = articlesArr.length ? (articlesArr.reduce((a, b) => a + b, 0) / articlesArr.length) : 0;
    const stdArticles = articlesArr.length ? ecartType(articlesArr) : 0;

    const moyMinutes = minutesArr.length ? (minutesArr.reduce((a, b) => a + b, 0) / minutesArr.length) : 0;
    const stdMinutes = minutesArr.length ? ecartType(minutesArr) : 0;

    return (
        <div style={{ padding: 20 }}>
            {/* Ligne principale : gauche = titre + filtres, droite = stats */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
               
                <div style={{ textAlign: "left" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <h1 style={{ margin: 0 }}>Suivi de production</h1>

                    </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
                        <ButtonGroup variant="outlined">
                            <Button onClick={() => handleChangePeriode("day")}>Jour</Button>
                            <Button onClick={() => handleChangePeriode("week")}>Semaine</Button>
                            <Button onClick={() => handleChangePeriode("month")}>Mois</Button>
                            <Button onClick={() => handleChangePeriode("year")}>Année</Button>
                        </ButtonGroup>
                        <DateSelector periode={periode} dateVal={dateVal} setDateVal={setDateVal} />
                        <RadioGroup row value={metric} onChange={(e) => setMetric(e.target.value)}>
                            <FormControlLabel value="articles" control={<Radio color="primary" />} label="Articles" />
                            <FormControlLabel value="temps" control={<Radio color="primary" />} label="Temps (h)" />
                        </RadioGroup>
                </div>
                {/* Droite : stats */}
                <div style={{ textAlign: "right" }}>
                    <div>
                        <strong>Classique :</strong> {data.recap.Classique.nb_articles} articles, {formatHeuresMinutes(data.recap.Classique.temps_total)}
                    </div>
                    <div>
                        <strong>SAV :</strong> {data.recap.SAV.nb_articles} articles, {formatHeuresMinutes(data.recap.SAV.temps_total)}
                    </div>
                    <div style={{ marginTop: 8 }}>
                        <strong>Total global :</strong> {totalArticles} articles, {formatHeuresMinutes(totalMinutes)}
                    </div>
                    <div style={{ marginTop: 8 }}>
                        <strong>Moyenne :</strong> {moyArticles.toFixed(2)} articles, {formatHeuresMinutes(moyMinutes)}
                    </div>
                    <div>
                        <strong>Écart-type :</strong> {stdArticles.toFixed(2)} articles, {formatHeuresMinutes(stdMinutes)}
                    </div>
                </div>
            </div>

            {/* Graphique */}
            <div style={{ marginTop: 30, display: "flex", justifyContent: "center" }}>
                <ProductionChart data={data} periode={periode} metric={metric} />
            </div>
        </div>
    );
};

const DateSelector = ({ periode, dateVal, setDateVal }) => {
    let type = "date";
    let value = dateVal;

    if (periode === "week") {
        type = "week";
        const date = new Date(dateVal);
        const year = date.getFullYear();
        const weekNumber = getNumSemaine(date);
        value = `${year}-W${weekNumber.toString().padStart(2, "0")}`;
    } else if (periode === "month") {
        type = "month";
    } else if (periode === "year") {
        type = "number";
    }

    const handleChange = (e) => {
        let newValue = e.target.value;
        if (periode === "week") {
            const [year, week] = newValue.split("-W");
            newValue = getDateFromWeekNumber(year, week);
        }
        setDateVal(newValue);
    };

    return <TextField variant="outlined" type={type} value={value} onChange={handleChange} />;
};

const ProductionChart = ({ data, periode, metric }) => {
    let groupedData = {};

    if (periode === "week") {
        const daysOfWeek = {};
        Object.keys(data.graph).forEach(dayStr => {
            const date = new Date(dayStr);
            const dayName = JOURS_FR[date.getDay()];
            daysOfWeek[dayName] = data.graph[dayStr];
        });
        groupedData = daysOfWeek;
    } else if (periode === "month") {
        Object.keys(data.graph).forEach(dayStr => {
            const d = new Date(dayStr);
            const semaine = getNumSemaine(d);
            const label = `S${semaine}`;
            if (!groupedData[label]) groupedData[label] = {};
            Object.entries(data.graph[dayStr]).forEach(([ouvrier, stats]) => {
                if (!groupedData[label][ouvrier]) groupedData[label][ouvrier] = { nb_articles: 0, temps_total: 0 };
                groupedData[label][ouvrier].nb_articles += stats.nb_articles;
                groupedData[label][ouvrier].temps_total += stats.temps_total;
            });
        });
    } else if (periode === "year") {
        Object.keys(data.graph).forEach(dayStr => {
            const d = new Date(dayStr);
            const label = MOIS_FR[d.getMonth()];
            if (!groupedData[label]) groupedData[label] = {};
            Object.entries(data.graph[dayStr]).forEach(([ouvrier, stats]) => {
                if (!groupedData[label][ouvrier]) groupedData[label][ouvrier] = { nb_articles: 0, temps_total: 0 };
                groupedData[label][ouvrier].nb_articles += stats.nb_articles;
                groupedData[label][ouvrier].temps_total += stats.temps_total;
            });
        });
    } else {
        groupedData = data.graph;
    }

    const labels = Object.keys(groupedData).sort((a, b) => {
        if (periode === "week") {
            const order = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
            return order.indexOf(a) - order.indexOf(b);
        } else if (periode === "month") {
            const numA = parseInt(a.replace("S", ""), 10);
            const numB = parseInt(b.replace("S", ""), 10);
            return numA - numB;
        } else if (periode === "year") {
            return MOIS_FR.indexOf(a) - MOIS_FR.indexOf(b);
        } else {
            return new Date(a) - new Date(b);
        }
    });

    const ouvriers = [...new Set(Object.values(groupedData).flatMap(day => Object.keys(day)))];
    const colors = {};
    ouvriers.forEach((ouv, idx) => (colors[ouv] = COULEURS[idx % COULEURS.length]));

    const datasets = ouvriers.map(ouv => ({
        label: ouv,
        backgroundColor: colors[ouv],
        data: labels.map(label =>
            metric === "articles"
                ? groupedData[label][ouv]?.nb_articles || 0
                : (groupedData[label][ouv]?.temps_total || 0) / 60
        ),
        stack: "Stack 0"
    }));

    const chartData = { labels, datasets };
    const options = {
        responsive: true,
        scales: {
            x: { stacked: true },
            y: { stacked: true, ticks: { beginAtZero: true } }
        },
        plugins: { legend: { position: "bottom" } }
    };

    return <Bar data={chartData} options={options} height={100} />;
};

// --- Helpers ---
function getDateParDefaut(periode) {
    const today = new Date();
    if (periode === "day") {
        return today.toISOString().slice(0, 10);
    } else if (periode === "week") {
        const lundi = new Date(today.setDate(today.getDate() - today.getDay() + 1));
        return lundi.toISOString().slice(0, 10);
    } else if (periode === "month") {
        return `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, "0")}`;
    } else if (periode === "year") {
        return today.getFullYear();
    }
}

function getNumSemaine(d) {
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

function getDateFromWeekNumber(year, week) {
    const date = new Date(year, 0, 1);
    const dayOfWeek = date.getDay();
    const isoWeekStart = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(year, 0, isoWeekStart + (week - 1) * 7);
    return monday.toISOString().slice(0, 10);
}

function ecartType(arr) {
    const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
    const variance = arr.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / arr.length;
    return Math.sqrt(variance);
}

function formatHeuresMinutes(minutes) {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return `${h}h ${m}min`;
}

export default SuiviProduction;
